import React, { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { roundToCents } from "@common/helpers/monetary";
import { ReverseApproveAccrualReviewBody } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { reverseApproveAccrualReview } from "@api/accrual-review.api";
import {
    hasTransactionsLeftAfterUnmatch,
    unmatchedSelectionItemIds,
} from "./schedule/matchResolutions";
import { useApproveWithScheduleResolutions } from "@components/accounting/schedules/linkedConflict/useApproveWithScheduleResolutions";
import {
    applyApproveToAccrualReviewCache,
    invalidateAccrualMatchCaches,
} from "@components/accounting/accrualReview/accrualReviewCache";
import { AccrualMatchSearchFooterState, MatchableItem } from "./types";
import { MatchesPanel } from "./MatchesPanel";
import {
    DocumentMatchDiff,
    getAllocationOpenTarget,
} from "./documentMatchDiff";
import {
    matchableItemToLockCheckItem,
    useIsAnyMatchableItemLocked,
} from "./matchLock";
import { useAccrualMatch } from "./useAccrualMatch";
import {
    getConfirmLabel,
    getDifferenceActionLabel,
    getRemovalConfirmLabel,
} from "./transactionMatchFooter.lib";

interface Props {
    documentType: "bill" | "invoice";
    documentId: string;
    sourceItem: MatchableItem;
    sourceOpenAmount: number;
    originalItems: MatchableItem[];
    diff: DocumentMatchDiff;
    searchFooterState: AccrualMatchSearchFooterState | null;
    onEditAllAllocations: (items: MatchableItem[]) => void;
    onMatchesPanelExtraHeightChange: (px: number) => void;
}

function getSourceLabel({
    documentType,
    documentNumber,
}: {
    documentType: Props["documentType"];
    documentNumber: MatchableItem["documentNumber"];
}): string {
    if (documentType === "bill") {
        return "Bill to match";
    }
    return documentNumber
        ? `Invoice ${documentNumber} to match`
        : "Invoice to match";
}

export const DocumentMatchFooter: React.FC<Props> = ({
    documentType,
    documentId,
    sourceItem,
    sourceOpenAmount,
    originalItems,
    diff,
    searchFooterState,
    onEditAllAllocations,
    onMatchesPanelExtraHeightChange,
}) => {
    const { selectedItems, removeItem, onOpenEditAllocation, onClose } =
        useAccrualMatch();
    const { workspaceId } = useScope();
    const { toast } = useToaster();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const isSubmittingRef = useRef(false);

    const hasChanges =
        diff.txIdsToAdd.length > 0 || diff.txIdsToRemove.length > 0;

    const lockCheckItems = useMemo(() => {
        const selectedIds = new Set(selectedItems.map((item) => item.id));
        const removedItems = originalItems.filter(
            (item) => !selectedIds.has(item.id),
        );
        return [sourceItem, ...selectedItems, ...removedItems].map(
            matchableItemToLockCheckItem,
        );
    }, [sourceItem, selectedItems, originalItems]);
    const { isConfirmBlocked: isLockBlocked, lockedReason: blockedReason } =
        useIsAnyMatchableItemLocked(lockCheckItems);

    const isConfirmEnabled = useMemo(() => {
        if (!hasChanges || isLoading || isLockBlocked) {
            return false;
        }
        if (diff.txIdsToAdd.length === 0) {
            return true;
        }
        const effectiveOpen = roundToCents(
            Math.abs(sourceOpenAmount) + diff.removedAmount,
        );
        return diff.addAmount <= effectiveOpen;
    }, [hasChanges, isLoading, isLockBlocked, sourceOpenAmount, diff]);

    const { approveWithScheduleResolutions, scheduleResolutionModals } =
        useApproveWithScheduleResolutions();

    const handleConfirm = useCallback(async () => {
        if (!workspaceId || isSubmittingRef.current || !isConfirmEnabled) {
            return;
        }

        isSubmittingRef.current = true;
        setIsLoading(true);
        try {
            const baseBody = {
                ...(diff.txIdsToAdd.length > 0
                    ? { transactionIds: diff.txIdsToAdd }
                    : {}),
                ...(diff.txIdsToRemove.length > 0
                    ? { removeTransactionIds: diff.txIdsToRemove }
                    : {}),
            };
            const body: ReverseApproveAccrualReviewBody =
                documentType === "invoice"
                    ? { ...baseBody, invoiceId: documentId }
                    : { ...baseBody, billId: documentId };

            await approveWithScheduleResolutions({
                request: ({ scheduleResolutions, extraHeaders }) =>
                    reverseApproveAccrualReview({
                        workspaceId,
                        body: { ...body, scheduleResolutions },
                        extraHeaders,
                    }),
                hasWorkLeft: (resolutions) =>
                    hasTransactionsLeftAfterUnmatch({
                        transactionIds: diff.txIdsToAdd,
                        removeTransactionIds: diff.txIdsToRemove,
                        resolutions,
                    }),
                onApproved: (response) => {
                    for (const tx of response.updatedTransactions) {
                        applyApproveToAccrualReviewCache(tx.id, response);
                    }
                    toast("Match saved");
                    onClose?.();
                },
                onUnmatched: (resolutions) => {
                    for (const itemId of unmatchedSelectionItemIds(
                        resolutions,
                    )) {
                        removeItem(itemId);
                    }
                },
            });
        } catch {
            // toasted by approveWithScheduleResolutions
        }
        invalidateAccrualMatchCaches(queryClient);
        isSubmittingRef.current = false;
        setIsLoading(false);
    }, [
        workspaceId,
        isConfirmEnabled,
        documentType,
        documentId,
        diff,
        queryClient,
        toast,
        onClose,
        removeItem,
        approveWithScheduleResolutions,
    ]);

    const handleUnmatch = useCallback(
        (item: MatchableItem) => removeItem(item.id),
        [removeItem],
    );

    const difference = roundToCents(sourceItem.openAmount ?? 0);
    const allocationTarget = getAllocationOpenTarget({
        diff,
        selectedItems,
    });
    const handleDifferenceAction = () => {
        if (allocationTarget.kind === "all") {
            onEditAllAllocations(selectedItems);
            return;
        }
        if (allocationTarget.kind === "single") {
            onOpenEditAllocation?.(selectedItems[0]);
        }
    };
    const differenceActionBlockedReason =
        allocationTarget.kind === "blocked" ? allocationTarget.reason : null;

    const isSavingRemoval =
        diff.txIdsToRemove.length > 0 && diff.txIdsToAdd.length === 0;
    const confirmLabel = isSavingRemoval
        ? getRemovalConfirmLabel()
        : getConfirmLabel({
              isCashBasis: false,
              isAlreadyApproved: false,
              isSelectionAlreadyPersisted: false,
          });
    const confirmDisabledTooltip =
        !isConfirmEnabled && blockedReason ? blockedReason : undefined;

    return (
        <MatchesPanel
            title="Matches to be confirmed"
            sourceLabel={getSourceLabel({
                documentType,
                documentNumber: sourceItem.documentNumber,
            })}
            sourceItem={sourceItem}
            matchesLabel="Matches"
            selectedItems={selectedItems}
            onUnmatch={handleUnmatch}
            difference={difference}
            blockedReason={blockedReason}
            searchFooterState={searchFooterState}
            onMatchesPanelExtraHeightChange={onMatchesPanelExtraHeightChange}
            actions={
                <>
                    <Tooltip
                        content={differenceActionBlockedReason ?? undefined}
                    >
                        <Button
                            variant="primary"
                            size="md"
                            disabled={differenceActionBlockedReason !== null}
                            onClick={handleDifferenceAction}
                        >
                            {getDifferenceActionLabel(difference)}
                        </Button>
                    </Tooltip>
                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyDisplayPosition="inline"
                        tooltip={confirmDisabledTooltip}
                        hotKeyOptions={{
                            description: confirmLabel,
                            enabled: isConfirmEnabled,
                            preventDefault: true,
                            enableOnFormTags: true,
                        }}
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                disabled={!isConfirmEnabled}
                                onClick={handleConfirm}
                                rightIcon={hotkeyHint}
                            >
                                {confirmLabel}
                            </Button>
                        )}
                    </WithHotkey>
                </>
            }
        >
            {scheduleResolutionModals}
        </MatchesPanel>
    );
};
